import React, { useCallback, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Grid,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import { conciliacionApi } from "src/services/conciliacionApi";
import Upload from "../upload/upload";
import { useSnackbar } from "src/components/snackbar";
import {
  BANCOS_DISPONIBLES,
  detectarBancoPorNombre,
} from "src/utils/conciliacionHelpers";
import { CheckCircle } from "@material-ui/icons";

const MAX_SIZE = 5 * 1024 * 1024; // 5MB

export default function ModalUploadCartola({ open, setOpen }) {
  const [file, setFile] = useState(null);
  const [bancoSeleccionado, setBancoSeleccionado] = useState("");
  const [bancoDetectado, setBancoDetectado] = useState(null);
  const [uploadCartola, { isLoading: uploading }] =
    conciliacionApi.useUploadCartolaMutation();
  const { enqueueSnackbar } = useSnackbar();

  const handleClose = useCallback(() => {
    if (!uploading) {
      setFile(null);
      setBancoSeleccionado("");
      setBancoDetectado(null);
      setOpen(false);
    }
  }, [setOpen, uploading]);

  const handleDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const archivo = acceptedFiles[0];
      setFile(archivo);

      // Detectar banco automáticamente por nombre de archivo
      const bancoAuto = detectarBancoPorNombre(archivo.name);
      setBancoDetectado(bancoAuto);

      // Pre-seleccionar si se detectó
      if (bancoAuto) {
        setBancoSeleccionado(bancoAuto);
      } else {
        setBancoSeleccionado(""); // Limpiar si no se detectó
      }
    }
  };

  const handleRemove = () => {
    setFile(null);
    setBancoSeleccionado("");
    setBancoDetectado(null);
  };

  const handleUpload = useCallback(async () => {
    if (!file) {
      enqueueSnackbar("Selecciona un archivo primero", {
        variant: "info",
      });
      return;
    }

    if (!bancoSeleccionado) {
      enqueueSnackbar("Debes seleccionar el banco antes de cargar", {
        variant: "info",
      });
      return;
    }

    try {
      // Crear FormData
      const formData = new FormData();
      formData.append("file", file);
      formData.append("banco", bancoSeleccionado);

      const response = await uploadCartola(formData).unwrap();

      const { status, estadisticas, mensaje } = response;

      // Verificar si es duplicado
      if (status === "duplicado") {
        enqueueSnackbar(
          mensaje || "Este archivo ya fue cargado anteriormente",
          {
            variant: "info",
          },
        );

        setFile(null);
        setBancoSeleccionado("");
        setBancoDetectado(null);
        return;
      }
      if (status === "success_sin_cambios") {
        enqueueSnackbar(
          mensaje ||
            "El archivo se cargó correctamente, pero los movimientos estaban duplicados",
          { variant: "info" },
        );
        setFile(null);
        setBancoSeleccionado("");
        setBancoDetectado(null);
        handleClose();
        return;
      }

      // Éxito
      const msgTransbank =
        estadisticas.ignorados_transbank > 0
          ? ` (${estadisticas.ignorados_transbank} Transbank ignorados)`
          : "";
      enqueueSnackbar(
        `Cartola cargada exitosamente. ${estadisticas.insertados} movimientos insertados${msgTransbank}`,
        { variant: "success" },
      );

      // Cerrar modal y limpiar
      setFile(null);
      setBancoSeleccionado("");
      setBancoDetectado(null);
      handleClose();
    } catch (error) {
      console.error("Error al cargar cartola:", error);

      const errorMessage =
        error.response?.data?.mensaje ||
        error.response?.data?.message ||
        "Error al cargar el archivo";

      enqueueSnackbar(errorMessage, {
        variant: "success",
      });
    }
  }, [bancoSeleccionado, enqueueSnackbar, file, handleClose, uploadCartola]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={uploading}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">📤 Cargar Cartola Bancaria</Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={uploading}
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Área de Upload */}
          <Grid item xs={12} md={8}>
            <Upload
              file={file}
              onDrop={handleDrop}
              onDelete={handleRemove}
              accept={{
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                  [".xlsx"],
                "application/vnd.ms-excel": [".xls"],
              }}
              maxSize={MAX_SIZE}
              disabled={uploading}
              helperText="Arrastra aquí tu archivo Excel o haz click para seleccionar"
            />

            {/* Selector de Banco (solo visible si hay archivo) */}
            {file && (
              <Box mt={3}>
                {/* Badge de detección automática */}
                {bancoDetectado && (
                  <Box mb={2}>
                    <Chip
                      icon={<CheckCircle style={{ fontSize: 16 }} />}
                      label={`✓ Banco detectado automáticamente: ${
                        BANCOS_DISPONIBLES.find(
                          (b) => b.value === bancoDetectado,
                        )?.label
                      }`}
                      size="small"
                      style={{
                        backgroundColor: "#e8f5e9",
                        color: "#2e7d32",
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                )}

                {/* Alert si NO se detectó */}
                {!bancoDetectado && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      No se pudo detectar el banco automáticamente. Por favor,
                      selecciónalo manualmente.
                    </Typography>
                  </Alert>
                )}

                {/* Selector de Banco */}
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="banco-select-label">
                    Banco * (Obligatorio)
                  </InputLabel>
                  <Select
                    labelId="banco-select-label"
                    id="banco-select"
                    value={bancoSeleccionado}
                    onChange={(e) => setBancoSeleccionado(e.target.value)}
                    label="Banco * (Obligatorio)"
                    disabled={uploading}
                  >
                    <MenuItem value="">
                      <em>-- Selecciona un banco --</em>
                    </MenuItem>
                    {BANCOS_DISPONIBLES.map((banco) => (
                      <MenuItem key={banco.value} value={banco.value}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>{banco.icon}</span>
                          <span>{banco.label}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Grid>

          {/* Instrucciones */}
          <Grid item xs={12} md={4}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                📋 Instrucciones:
              </Typography>
              <Typography variant="body2" component="div">
                1. Descarga la cartola desde tu banco
                <br />
                2. Arrastra el archivo Excel aquí
                <br />
                3. El sistema intentará detectar el banco
                <br />
                4. Verifica o selecciona el banco correcto
                <br />
                5. Click en "Cargar Cartola"
              </Typography>
            </Alert>

            <Alert severity="warning">
              <Typography variant="subtitle2" gutterBottom>
                ⚠️ Importante:
              </Typography>
              <Typography variant="body2">
                No modifiques el formato del archivo descargado del banco.
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={uploading} color="inherit">
          Cancelar
        </Button>
        <LoadingButton
          onClick={handleUpload}
          loading={uploading}
          variant="contained"
          disabled={!file}
        >
          Cargar Cartola
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
